/**
 * Scenario Wells View component for viewing scenario wells
 */
import { Visibility } from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { ScenarioLayer } from '../api/scenarioLayers';
import { ScenarioWell } from '../api/scenarioWells';
import { ScenarioResponse } from '../types/scenario';
import { ScenarioWellDetailModal } from './ScenarioWellDetailModal';

interface ScenarioWellsViewProps {
  scenarioId: string;
  wells: ScenarioWell[];
  layers: ScenarioLayer[];
  scenario: ScenarioResponse;
  onBack: () => void;
}

export const ScenarioWellsView: React.FC<ScenarioWellsViewProps> = ({
  scenarioId,
  wells,
  layers,
  scenario,
  onBack,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedWell, setSelectedWell] = useState<ScenarioWell | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const handleViewWellDetails = (well: ScenarioWell) => {
    setSelectedWell(well);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedWell(null);
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      oil: '#ff9800',
      gas: '#2196f3',
      production: '#4caf50',
      injection: '#9c27b0',
      observation: '#607d8b',
    };
    return colors[type] || '#757575';
  };

  const getWellTypeStats = () => {
    const stats: Record<string, number> = {};
    wells.forEach(well => {
      const type = well.well_type?.name || 'Unknown';
      stats[type] = (stats[type] || 0) + 1;
    });
    return stats;
  };

  const getLayerName = (layerId: string) => {
    const layer = layers.find(l => l.scenario_layer_id === layerId);
    return layer?.layer_name || 'Unknown Layer';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getWellTypeLabel = (type: string) => {
    if (isMobile && type.toLowerCase() === 'production') return 'P';
    return type;
  };

  const wellTypeStats = getWellTypeStats();

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {wells.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Wells
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {Object.entries(wellTypeStats).map(([type, count]) => (
          <Grid item xs={12} sm={6} md={3} key={type}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getWellTypeLabel(type)} Wells
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Wells Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scenario Wells
          </Typography>

          {wells.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No wells added to this scenario yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Well Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reservoir</TableCell>
                    <TableCell>Entry Date</TableCell>
                    <TableCell align="center" sx={{ display: 'none' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wells.map((well) => (
                    <TableRow key={well.scenario_well_id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {well.well?.name || well.well_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getWellTypeLabel(well.well_type?.name || 'Unknown')}
                          sx={{
                            backgroundColor: getTypeColor(well.well_type?.name || ''),
                            color: 'white',
                            fontWeight: 'medium',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getLayerName(well.scenario_layer_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(well.entry_date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ display: 'none' }}>
                        <IconButton
                          onClick={() => handleViewWellDetails(well)}
                          color="primary"
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Well Detail Modal */}
      {selectedWell && (
        <ScenarioWellDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          well={selectedWell}
          readOnly={true}
        />
      )}
    </Box>
  );
};
